/**
 * Responsibility: add a card to the grading list.
 *
 * The set name is pushed hard for one reason: a bare "Charizard" matches
 * thousands of printings whose prices differ by three orders of magnitude,
 * and the feature would then produce a confident number for a card that does
 * not exist. It is not required - the operator may genuinely want a loose
 * search - but the form says why it matters rather than leaving a blank box.
 *
 * There is no price field. Prices come from eBay on add, and a hand-entered
 * price would be indistinguishable from a fetched one in the table unless it
 * carried its own basis.
 *
 * The PSA spec ID is offered here because it is the only way to get a
 * population automatically - PSA's report is keyed on that number and their
 * API has no name search - and because entering it at the same time as the
 * card saves a second trip through the detail panel.
 */
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { CandidateCreate } from '../../api/client';

type AddCandidateDialogProps = {
  open: boolean;
  pending?: boolean;
  error?: string | null;
  soldPricesAvailable: boolean;
  populationAvailable: boolean;
  onClose: () => void;
  onSubmit: (payload: CandidateCreate) => void;
};

export function AddCandidateDialog({
  open,
  pending = false,
  error = null,
  soldPricesAvailable,
  populationAvailable,
  onClose,
  onSubmit,
}: AddCandidateDialogProps) {
  const [cardName, setCardName] = useState('');
  const [setName, setSetName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [specId, setSpecId] = useState('');

  const canSubmit = cardName.trim().length > 0 && !pending;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      card_name: cardName.trim(),
      set_name: setName.trim(),
      card_number: cardNumber.trim(),
      psa_spec_id: specId.trim() || null,
    });
  };

  const close = () => {
    setCardName('');
    setSetName('');
    setCardNumber('');
    setSpecId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Consider a card</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {!soldPricesAvailable ? (
            <Alert severity="warning">
              Sold prices are unavailable, so this card will be priced from live listings. Sellers
              ask well above what copies actually go for, and the row will say so.
            </Alert>
          ) : null}

          <TextField
            label="Card"
            placeholder="Charizard"
            value={cardName}
            onChange={(event) => setCardName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
            autoFocus
            fullWidth
            required
          />

          <TextField
            label="Set"
            placeholder="Celebrations"
            value={setName}
            onChange={(event) => setSetName(event.target.value)}
            helperText="Strongly recommended. Without it, prices may come from a different printing entirely."
            fullWidth
          />

          <TextField
            label="Card number"
            placeholder="4/102"
            value={cardNumber}
            onChange={(event) => setCardNumber(event.target.value)}
            fullWidth
          />

          <TextField
            label="PSA spec ID"
            placeholder="12345"
            value={specId}
            onChange={(event) => setSpecId(event.target.value)}
            helperText={
              populationAvailable
                ? "Optional. From PSA's population report URL. With it, the gem rate is fetched automatically."
                : 'Optional, and not usable yet - no PSA credential is configured, so counts have to be typed in.'
            }
            fullWidth
          />

          <Typography variant="caption" color="text.secondary">
            Prices are fetched as soon as the card is added. If nothing matching has sold, the card
            still appears, with the reason.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button size="small" onClick={close}>Cancel</Button>
        <Button size="small" variant="contained" onClick={submit} disabled={!canSubmit}>
          {pending ? 'Pricing' : 'Add and price'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
